
import React from 'react';
import { useTimeSlots } from '@/hooks/useTimeSlots';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';

interface TimeSlotViewerProps {
  providerId: string;
  serviceId?: string;
}

export const TimeSlotViewer: React.FC<TimeSlotViewerProps> = ({ 
  providerId, 
  serviceId 
}) => {
  const { data: timeSlots, isLoading, error } = useTimeSlots(providerId, serviceId);

  console.log('TimeSlotViewer: Loaded time slots', {
    count: timeSlots?.length || 0,
    slots: timeSlots?.map(slot => ({
      id: slot.id,
      start: slot.start_time,
      current: slot.current_bookings,
      max: slot.max_bookings,
      available: slot.is_available
    }))
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="text-red-600">
            Error loading time slots: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!timeSlots || timeSlots.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="text-gray-600 text-center">
            No time slots found for this provider.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Time Slots Overview</h3>
      
      <div className="grid gap-4">
        {timeSlots.map((slot) => {
          const startTime = new Date(slot.start_time);
          const endTime = new Date(slot.end_time);
          const isFullyBooked = slot.current_bookings >= slot.max_bookings;
          
          return (
            <Card key={slot.id} className={`${isFullyBooked ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(startTime, 'EEE, MMM d')}
                  </div>
                  <Badge variant={isFullyBooked ? 'destructive' : 'default'}>
                    {isFullyBooked ? 'Fully Booked' : 'Available'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span>
                      {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-500" />
                    <span>
                      {slot.current_bookings}/{slot.max_bookings} booked
                    </span>
                  </div>
                </div>
                
                {slot.service && (
                  <div className="mt-2 text-xs text-gray-600">
                    Service: {slot.service.name}
                  </div>
                )}
                
                {slot.room && (
                  <div className="mt-1 text-xs text-gray-600">
                    Room: {slot.room.name}
                  </div>
                )}
                
                <div className="mt-2 text-xs text-gray-500">
                  Slot ID: {slot.id}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
